import { useState, useEffect } from 'react';
import { ChartBar, Users, UserPlus, Lock, Eye, TrendUp, SignOut, CurrencyDollar, Target, Funnel, ArrowUp, ArrowDown } from '@phosphor-icons/react';

export default function AdminDashboard() {
  const [password, setPassword] = useState('');
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('overview');

  const fetchStats = async (pw, d) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/stats?password=${encodeURIComponent(pw)}&days=${d}`);
      if (res.status === 401) { setError('Wrong password'); setAuthed(false); setLoading(false); return; }
      const data = await res.json();
      if (data.error) { setError(data.error); } else { setStats(data); setAuthed(true); }
    } catch (e) { setError('Failed to load stats'); }
    setLoading(false);
  };

  const handleLogin = (e) => { e.preventDefault(); fetchStats(password, days); };
  useEffect(() => { if (authed) fetchStats(password, days); }, [days]);

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#191E29] flex items-center justify-center px-4">
        <form onSubmit={handleLogin} className="bg-[#0f1319] border border-white/10 rounded-2xl p-8 w-full max-w-sm space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <Lock size={24} weight="duotone" className="text-[#07c28e]" />
            <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
          </div>
          <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 bg-[#191E29] border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-[#07c28e] focus:outline-none" />
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <button type="submit" className="w-full py-3 bg-[#07c28e] text-white font-bold rounded-xl hover:bg-[#06a879] transition-colors">
            {loading ? 'Loading...' : 'Login'}
          </button>
        </form>
      </div>
    );
  }

  const t = stats?.totals || {};
  const convRate = stats?.conversion_rate || '0%';
  const dropoff = stats?.dropoff_by_step || {};
  const daily = stats?.daily || [];

  // Calculate CRO metrics
  const viewToStart = t.page_views > 0 ? ((t.form_starts / t.page_views) * 100).toFixed(1) : '0.0';
  const startToSubmit = t.form_starts > 0 ? ((t.form_submits / t.form_starts) * 100).toFixed(1) : '0.0';
  const viewToSubmit = t.page_views > 0 ? ((t.form_submits / t.page_views) * 100).toFixed(1) : '0.0';

  // Chart data
  const maxViews = Math.max(...daily.map(d => d.page_views), 1);
  const maxStarts = Math.max(...daily.map(d => d.form_starts), 1);
  const maxSubmits = Math.max(...daily.map(d => d.form_submits), 1);

  // Week over week comparison
  const thisWeek = daily.slice(-7);
  const lastWeek = daily.slice(-14, -7);
  const thisWeekSubmits = thisWeek.reduce((s, d) => s + (d.form_submits || 0), 0);
  const lastWeekSubmits = lastWeek.reduce((s, d) => s + (d.form_submits || 0), 0);
  const wowChange = lastWeekSubmits > 0 ? (((thisWeekSubmits - lastWeekSubmits) / lastWeekSubmits) * 100).toFixed(0) : thisWeekSubmits > 0 ? '+100' : '0';
  const thisWeekStarts = thisWeek.reduce((s, d) => s + (d.form_starts || 0), 0);
  const lastWeekStarts = lastWeek.reduce((s, d) => s + (d.form_starts || 0), 0);
  const wowStartsChange = lastWeekStarts > 0 ? (((thisWeekStarts - lastWeekStarts) / lastWeekStarts) * 100).toFixed(0) : thisWeekStarts > 0 ? '+100' : '0';

  return (
    <div className="min-h-screen bg-[#191E29] text-white px-4 sm:px-6 py-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <div className="flex items-center gap-3">
            <ChartBar size={28} weight="duotone" className="text-[#07c28e]" />
            <h1 className="text-2xl font-bold">Ship Velocity Analytics</h1>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            {[7, 14, 30, 60, 90].map((d) => (
              <button key={d} onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${days === d ? 'bg-[#07c28e] text-white' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}>
                {d}d
              </button>
            ))}
            <button onClick={() => { setAuthed(false); setPassword(''); setStats(null); }}
              className="ml-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all">
              <SignOut size={14} /> Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-[#0f1319] rounded-xl p-1 w-fit">
          {[
            { key: 'overview', label: 'Overview', icon: Eye },
            { key: 'funnel', label: 'Funnel & CRO', icon: Funnel },
            { key: 'charts', label: 'Charts', icon: ChartBar },
          ].map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${tab === key ? 'bg-[#07c28e] text-white' : 'text-white/40 hover:text-white/60'}`}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {/* ─── OVERVIEW TAB ─── */}
        {tab === 'overview' && (
          <>
            {/* Top Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard icon={Eye} label="Page Views" value={t.page_views} color="#94a3b8" />
              <StatCard icon={Users} label="Form Starts" value={t.form_starts} color="#3b82f6" wow={wowStartsChange} />
              <StatCard icon={UserPlus} label="Submissions" value={t.form_submits} color="#07c28e" wow={wowChange} />
              <StatCard icon={TrendUp} label="Conversion" value={convRate} color="#f59e0b" large />
            </div>

            {/* Daily Table */}
            <div className="bg-[#0f1319] border border-white/8 rounded-2xl p-6 overflow-x-auto">
              <h2 className="text-lg font-bold mb-4">Daily Breakdown</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/40 text-xs uppercase tracking-wider">
                    <th className="text-left py-3 px-3">Date</th>
                    <th className="text-right py-3 px-3">Views</th>
                    <th className="text-right py-3 px-3">Starts</th>
                    <th className="text-right py-3 px-3">S1</th>
                    <th className="text-right py-3 px-3">S2</th>
                    <th className="text-right py-3 px-3">S3</th>
                    <th className="text-right py-3 px-3">Submits</th>
                    <th className="text-right py-3 px-3">CVR</th>
                  </tr>
                </thead>
                <tbody>
                  {daily.map((d, i) => {
                    const rate = d.form_starts > 0 ? ((d.form_submits / d.form_starts) * 100).toFixed(0) : '0';
                    return (
                      <tr key={d.date} className={i % 2 === 0 ? 'bg-white/[0.02]' : ''}>
                        <td className="py-2.5 px-3 text-white/60 font-mono text-xs">{d.date}</td>
                        <td className="py-2.5 px-3 text-right text-white/50">{d.page_views}</td>
                        <td className="py-2.5 px-3 text-right text-blue-400">{d.form_starts}</td>
                        <td className="py-2.5 px-3 text-right text-white/40">{d.step_1}</td>
                        <td className="py-2.5 px-3 text-right text-white/40">{d.step_2}</td>
                        <td className="py-2.5 px-3 text-right text-white/40">{d.step_3}</td>
                        <td className="py-2.5 px-3 text-right text-[#07c28e] font-bold">{d.form_submits}</td>
                        <td className="py-2.5 px-3 text-right text-amber-400 font-mono">{rate}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ─── FUNNEL & CRO TAB ─── */}
        {tab === 'funnel' && (
          <>
            {/* CRO Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-[#0f1319] border border-white/8 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-1">
                  <Target size={16} className="text-blue-400" />
                  <span className="text-xs text-white/40 uppercase tracking-wider">View → Start Rate</span>
                </div>
                <p className="font-mono text-3xl font-bold text-blue-400">{viewToStart}%</p>
                <p className="text-xs text-white/30 mt-1">Of page visitors who engage with the form</p>
              </div>
              <div className="bg-[#0f1319] border border-white/8 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-1">
                  <Funnel size={16} className="text-[#07c28e]" />
                  <span className="text-xs text-white/40 uppercase tracking-wider">Start → Submit Rate</span>
                </div>
                <p className="font-mono text-3xl font-bold text-[#07c28e]">{startToSubmit}%</p>
                <p className="text-xs text-white/30 mt-1">Form completion rate once started</p>
              </div>
              <div className="bg-[#0f1319] border border-white/8 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-1">
                  <CurrencyDollar size={16} className="text-amber-400" />
                  <span className="text-xs text-white/40 uppercase tracking-wider">View → Submit Rate</span>
                </div>
                <p className="font-mono text-3xl font-bold text-amber-400">{viewToSubmit}%</p>
                <p className="text-xs text-white/30 mt-1">Overall landing page conversion</p>
              </div>
            </div>

            {/* Funnel Visualization */}
            <div className="bg-[#0f1319] border border-white/8 rounded-2xl p-6 mb-8">
              <h2 className="text-lg font-bold mb-6">Conversion Funnel</h2>
              <div className="space-y-4">
                <FunnelStep label="Page Views" count={t.page_views} max={t.page_views} color="#94a3b8" pct="100%" />
                <FunnelArrow pct={viewToStart + '%'} />
                <FunnelStep label="Form Started (clicked a service)" count={t.form_starts} max={t.page_views} color="#3b82f6" pct={viewToStart + '%'} />
                <FunnelArrow pct={dropoff.start_to_step1 + '%'} />
                <FunnelStep label="Step 1 → Selected Platform" count={t.step_1} max={t.page_views} color="#6366f1" pct={t.page_views > 0 ? ((t.step_1/t.page_views)*100).toFixed(1)+'%' : '0%'} />
                <FunnelArrow pct={dropoff.step1_to_step2 + '%'} />
                <FunnelStep label="Step 2 → Selected Volume" count={t.step_2} max={t.page_views} color="#8b5cf6" pct={t.page_views > 0 ? ((t.step_2/t.page_views)*100).toFixed(1)+'%' : '0%'} />
                <FunnelArrow pct={dropoff.step2_to_step3 + '%'} />
                <FunnelStep label="Step 3 → Entered Contact Info" count={t.step_3} max={t.page_views} color="#a855f7" pct={t.page_views > 0 ? ((t.step_3/t.page_views)*100).toFixed(1)+'%' : '0%'} />
                <FunnelArrow pct={dropoff.step3_to_submit + '%'} />
                <FunnelStep label="Submitted (Lead Captured)" count={t.form_submits} max={t.page_views} color="#07c28e" pct={viewToSubmit + '%'} />
              </div>
            </div>

            {/* Drop-off Analysis */}
            <div className="bg-[#0f1319] border border-white/8 rounded-2xl p-6">
              <h2 className="text-lg font-bold mb-4">Drop-off Analysis</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <DropoffCard from="Services" to="Platforms" rate={dropoff.start_to_step1} lost={t.form_starts - t.step_1} />
                <DropoffCard from="Platforms" to="Volume" rate={dropoff.step1_to_step2} lost={t.step_1 - t.step_2} />
                <DropoffCard from="Volume" to="Contact" rate={dropoff.step2_to_step3} lost={t.step_2 - t.step_3} />
                <DropoffCard from="Contact" to="Submit" rate={dropoff.step3_to_submit} lost={t.step_3 - t.form_submits} />
              </div>
            </div>
          </>
        )}

        {/* ─── CHARTS TAB ─── */}
        {tab === 'charts' && (
          <>
            {/* Page Views Chart */}
            <div className="bg-[#0f1319] border border-white/8 rounded-2xl p-6 mb-6">
              <h2 className="text-lg font-bold mb-4">Page Views</h2>
              <div className="flex items-end gap-[2px] h-48">
                {daily.map((d) => (
                  <div key={d.date} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                    <div className="absolute -top-8 bg-[#191E29] border border-white/10 rounded-lg px-2 py-1 text-[10px] text-white hidden group-hover:block whitespace-nowrap z-10">
                      {d.date}: {d.page_views} views
                    </div>
                    <div className="w-full rounded-t-sm bg-slate-500/60 transition-all hover:bg-slate-400" style={{ height: `${Math.max(2, (d.page_views / maxViews) * 100)}%` }} />
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-white/20">
                <span>{daily[0]?.date}</span>
                <span>{daily[daily.length - 1]?.date}</span>
              </div>
            </div>

            {/* Form Starts vs Submits Chart */}
            <div className="bg-[#0f1319] border border-white/8 rounded-2xl p-6 mb-6">
              <div className="flex items-center gap-6 mb-4">
                <h2 className="text-lg font-bold">Starts vs Submissions</h2>
                <div className="flex items-center gap-4 text-xs">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-blue-500" /> Starts</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#07c28e]" /> Submits</span>
                </div>
              </div>
              <div className="flex items-end gap-[2px] h-48">
                {daily.map((d) => {
                  const maxVal = Math.max(maxStarts, maxSubmits, 1);
                  return (
                    <div key={d.date} className="flex-1 flex items-end gap-[1px] h-full group relative">
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-[#191E29] border border-white/10 rounded-lg px-2 py-1 text-[10px] text-white hidden group-hover:block whitespace-nowrap z-10">
                        {d.date}: {d.form_starts}S / {d.form_submits}C
                      </div>
                      <div className="flex-1 rounded-t-sm bg-blue-500/50 hover:bg-blue-500/80 transition-all" style={{ height: `${Math.max(2, (d.form_starts / maxVal) * 100)}%` }} />
                      <div className="flex-1 rounded-t-sm bg-[#07c28e]/60 hover:bg-[#07c28e] transition-all" style={{ height: `${Math.max(2, (d.form_submits / maxVal) * 100)}%` }} />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-white/20">
                <span>{daily[0]?.date}</span>
                <span>{daily[daily.length - 1]?.date}</span>
              </div>
            </div>

            {/* Conversion Rate Over Time */}
            <div className="bg-[#0f1319] border border-white/8 rounded-2xl p-6">
              <h2 className="text-lg font-bold mb-4">Daily Conversion Rate</h2>
              <div className="flex items-end gap-[2px] h-48">
                {daily.map((d) => {
                  const rate = d.form_starts > 0 ? (d.form_submits / d.form_starts) * 100 : 0;
                  return (
                    <div key={d.date} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                      <div className="absolute -top-8 bg-[#191E29] border border-white/10 rounded-lg px-2 py-1 text-[10px] text-white hidden group-hover:block whitespace-nowrap z-10">
                        {d.date}: {rate.toFixed(0)}% CVR
                      </div>
                      <div className="w-full rounded-t-sm transition-all hover:opacity-80" style={{
                        height: `${Math.max(2, rate)}%`,
                        background: rate >= 50 ? '#07c28e' : rate >= 25 ? '#f59e0b' : rate > 0 ? '#ef4444' : '#1e293b'
                      }} />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-white/20">
                <span>{daily[0]?.date}</span>
                <span>{daily[daily.length - 1]?.date}</span>
              </div>
              <div className="flex gap-4 mt-3 text-[10px]">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#07c28e]" /> 50%+</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> 25-50%</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> &lt;25%</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, large, wow }) {
  const isPositive = wow && parseInt(wow) > 0;
  const isNegative = wow && parseInt(wow) < 0;
  return (
    <div className="bg-[#0f1319] border border-white/8 rounded-xl p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon size={18} weight="duotone" style={{ color }} />
          <span className="text-xs text-white/40 uppercase tracking-wider">{label}</span>
        </div>
        {wow && wow !== '0' && (
          <span className={`flex items-center gap-0.5 text-[10px] font-bold px-2 py-0.5 rounded-full ${isPositive ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
            {isPositive ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
            {Math.abs(parseInt(wow))}% WoW
          </span>
        )}
      </div>
      <p className={`font-mono font-bold ${large ? 'text-3xl' : 'text-2xl'}`} style={{ color }}>{value ?? 0}</p>
    </div>
  );
}

function FunnelStep({ label, count, max, color, pct }) {
  const width = max > 0 ? Math.max(6, (count / max) * 100) : 6;
  return (
    <div className="flex items-center gap-4">
      <span className="text-sm text-white/50 w-52 flex-shrink-0">{label}</span>
      <div className="flex-1 h-10 bg-white/5 rounded-lg overflow-hidden relative">
        <div className="h-full rounded-lg transition-all duration-500" style={{ width: `${width}%`, background: color }} />
        <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white/80">{count ?? 0}</span>
      </div>
      <span className="text-xs font-mono w-14 text-right" style={{ color }}>{pct}</span>
    </div>
  );
}

function FunnelArrow({ pct }) {
  return (
    <div className="flex items-center gap-4 pl-52 ml-4">
      <div className="text-white/20 text-lg">↓</div>
      <span className="text-[10px] text-white/30 font-mono">{pct} continued</span>
    </div>
  );
}

function DropoffCard({ from, to, rate, lost }) {
  const r = parseFloat(rate) || 0;
  const dropRate = (100 - r).toFixed(1);
  return (
    <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4">
      <p className="text-xs text-white/40 mb-1">{from} → {to}</p>
      <div className="flex items-end justify-between">
        <div>
          <p className="font-mono text-xl font-bold" style={{ color: r >= 80 ? '#07c28e' : r >= 50 ? '#f59e0b' : '#ef4444' }}>{rate}%</p>
          <p className="text-[10px] text-white/20">pass-through</p>
        </div>
        <div className="text-right">
          <p className="font-mono text-sm text-red-400/60">{lost > 0 ? `-${lost}` : '0'}</p>
          <p className="text-[10px] text-white/20">{dropRate}% dropped</p>
        </div>
      </div>
    </div>
  );
}
